import { type DocumentFile, getExpirationStatus } from "../models/document";
// @ts-expect-error pure js
import { FileStatus } from "../models/documents";
// @ts-expect-error pure js
import { SignatureContratStatus } from "../models/signatureElectronique";

export type DocumentListSortKey = "title" | "status" | "date";
export type SortDirection = "asc" | "desc";

export interface DocumentListSort {
  key: DocumentListSortKey;
  direction: SortDirection;
}

export interface DocumentListEntry {
  key: string;
  /** Dossier (plusieurs fichiers d'un même type), fichier seul, ou diagnostic attendu sans fichier. */
  kind: "folder" | "file" | "missing";
  title: string;
  files: DocumentFile[];
}

/** Rang de gravité d'un statut, du plus urgent au plus anodin ; sert au tri de la colonne Statut. */
export const STATUS_RANK = {
  expired: 0,
  missing: 1,
  expiring: 2,
  toValidate: 3,
  signing: 4,
  signed: 5,
  valid: 6,
  inherited: 7,
  none: 8,
} as const;

const SIGNING_STATUSES = [
  SignatureContratStatus.Opened.status,
  SignatureContratStatus.Open.status,
];

export function getFileStatusRank(
  file: DocumentFile,
  inherited = false,
): number {
  const expiration = getExpirationStatus(file.valid_until);
  if (expiration === "expired") return STATUS_RANK.expired;
  if (expiration === "expiring") return STATUS_RANK.expiring;
  if (file.status === FileStatus.aValider.status) return STATUS_RANK.toValidate;
  if (SIGNING_STATUSES.includes(file.signature_electronique_status)) {
    return STATUS_RANK.signing;
  }
  if (
    file.signature_electronique_status ===
    SignatureContratStatus.Archived.status
  ) {
    return STATUS_RANK.signed;
  }
  if (file.valid_until) return STATUS_RANK.valid;
  if (inherited) return STATUS_RANK.inherited;
  return STATUS_RANK.none;
}

function entryStatusRank(entry: DocumentListEntry): number {
  if (entry.kind === "missing") return STATUS_RANK.missing;
  return Math.min(...entry.files.map((file) => getFileStatusRank(file)));
}

function fileDate(file: DocumentFile): string {
  return file.date_add ?? "";
}

/** Date la plus récente du dossier, pour le tri et l'affichage de la ligne de dossier. */
export function latestFileDate(files: DocumentFile[]): string | undefined {
  const dates = files.map(fileDate).filter(Boolean);
  return dates.length ? dates.reduce((a, b) => (a > b ? a : b)) : undefined;
}

const collator = new Intl.Collator("fr", {
  numeric: true,
  sensitivity: "base",
});

export function sortFiles(
  files: DocumentFile[],
  sort: DocumentListSort | null,
): DocumentFile[] {
  if (!sort) return files;
  const sign = sort.direction === "asc" ? 1 : -1;
  const compare: Record<
    DocumentListSortKey,
    (a: DocumentFile, b: DocumentFile) => number
  > = {
    title: (a, b) => collator.compare(a.filename ?? "", b.filename ?? ""),
    date: (a, b) => fileDate(a).localeCompare(fileDate(b)),
    status: (a, b) => getFileStatusRank(a) - getFileStatusRank(b),
  };
  return [...files].sort((a, b) => sign * compare[sort.key](a, b));
}

/** Trie les lignes de premier niveau, dossiers et fichiers mêlés ; sans tri, l'ordre d'origine est conservé. */
export function sortEntries(
  entries: DocumentListEntry[],
  sort: DocumentListSort | null,
): DocumentListEntry[] {
  if (!sort) return entries;
  const sign = sort.direction === "asc" ? 1 : -1;
  const compare: Record<
    DocumentListSortKey,
    (a: DocumentListEntry, b: DocumentListEntry) => number
  > = {
    title: (a, b) => collator.compare(a.title, b.title),
    date: (a, b) =>
      (latestFileDate(a.files) ?? "").localeCompare(
        latestFileDate(b.files) ?? "",
      ),
    status: (a, b) => entryStatusRank(a) - entryStatusRank(b),
  };
  return [...entries].sort(
    (a, b) =>
      sign * compare[sort.key](a, b) || collator.compare(a.title, b.title),
  );
}

/** Un clic sur la colonne déjà triée inverse le sens ; un troisième clic revient à l'ordre par défaut. */
export function nextSort(
  current: DocumentListSort | null,
  key: DocumentListSortKey,
): DocumentListSort | null {
  if (current?.key !== key) return { key, direction: "asc" };
  if (current.direction === "asc") return { key, direction: "desc" };
  return null;
}
